import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, DollarSign, FileText, Check, Search, ChevronDown, ChevronRight } from 'lucide-react';
import api, { formatCurrency } from '../services/api';

const FY_OPTIONS = ['2025-26', '2024-25', '2023-24'];

export default function TDSModule() {
  const [sections, setSections] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [fy, setFy] = useState('2025-26');
  const [tab, setTab] = useState('entries');
  const [form, setForm] = useState({
    section: '194C', party_name: '', party_pan: '', party_type: 'individual',
    base_amount: '', date: new Date().toISOString().split('T')[0], narration: '', financial_year: '2025-26'
  });
  const [loading, setLoading] = useState(false);
  const [depositForm, setDepositForm] = useState(null);

  const loadEntries = useCallback(
    () => api.get('/tds/entries', { params: { fy, limit: 50 } }).then(r => setEntries(r.data.entries || [])).catch(() => {}),
    [fy]
  );
  const loadSummary = useCallback(
    () => api.get('/tds/summary', { params: { fy } }).then(r => setSummary(r.data)).catch(() => {}),
    [fy]
  );

  useEffect(() => {
    api.get('/tds/sections').then(r => setSections(r.data)).catch(() => {});
    loadEntries();
    loadSummary();
  }, [loadEntries, loadSummary]);

  const u = (f, v) => setForm(s => ({ ...s, [f]: v }));

  const selectedSection = sections.find(s => s.code === form.section);
  const calcRate = selectedSection ? (form.party_type === 'company' ? selectedSection.rate_company : selectedSection.rate_individual) : 0;
  const calcTds = form.base_amount ? Math.round(parseFloat(form.base_amount) * calcRate / 100 * 100) / 100 : 0;

  const handleCreate = async () => {
    if (!form.party_name || !form.base_amount) { toast.error('Fill party name and amount'); return; }
    setLoading(true);
    try {
      await api.post('/tds/entries', form);
      toast.success('TDS entry created'); setShowForm(false);
      setForm({ section: '194C', party_name: '', party_pan: '', party_type: 'individual', base_amount: '', date: new Date().toISOString().split('T')[0], narration: '', financial_year: fy });
      loadEntries(); loadSummary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleDeposit = async (entry_id) => {
    try {
      await api.post(`/tds/entries/${entry_id}/deposit`, depositForm);
      toast.success('TDS marked as deposited'); setDepositForm(null); loadEntries(); loadSummary();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="tds-module">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">TDS Management</h1>
          <p className="text-sm text-slate-500">Tax Deducted at Source tracking and compliance</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={fy} onChange={e => setFy(e.target.value)} className="form-input w-36" data-testid="fy-select">
            {FY_OPTIONS.map(f => <option key={f} value={f}>FY {f}</option>)}
          </select>
          <button onClick={() => setShowForm(true)} className="btn-accent flex items-center gap-2" data-testid="new-tds-btn"><Plus className="w-4 h-4" /> New TDS Entry</button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {[['entries', 'Entries'], ['summary', 'Summary'], ['sections', 'TDS Sections']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{l}</button>
        ))}
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <h3 className="font-heading font-semibold text-slate-800">New TDS Deduction</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className="form-label">Section</label><select value={form.section} onChange={e => u('section', e.target.value)} className="form-input" data-testid="tds-section">{sections.map(s => <option key={s.code} value={s.code}>{s.code} - {s.description}</option>)}</select></div>
            <div><label className="form-label">Party Name</label><input type="text" value={form.party_name} onChange={e => u('party_name', e.target.value)} className="form-input" data-testid="tds-party" /></div>
            <div><label className="form-label">PAN</label><input type="text" value={form.party_pan} onChange={e => u('party_pan', e.target.value)} className="form-input" placeholder="ABCDE1234F" /></div>
            <div><label className="form-label">Party Type</label><select value={form.party_type} onChange={e => u('party_type', e.target.value)} className="form-input"><option value="individual">Individual/HUF</option><option value="company">Company</option></select></div>
            <div><label className="form-label">Base Amount</label><input type="number" value={form.base_amount} onChange={e => u('base_amount', e.target.value)} className="form-input font-mono text-right" data-testid="tds-amount" /></div>
            <div><label className="form-label">Date</label><input type="date" value={form.date} onChange={e => u('date', e.target.value)} className="form-input" /></div>
            <div className="bg-orange-50 rounded-md p-3 flex flex-col justify-center"><p className="text-xs text-slate-500">TDS Rate: {calcRate}%</p><p className="font-mono font-bold text-orange-600">TDS: Rs. {formatCurrency(calcTds)}</p></div>
            <div><label className="form-label">Narration</label><input type="text" value={form.narration} onChange={e => u('narration', e.target.value)} className="form-input" /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={loading} className="btn-accent" data-testid="save-tds-btn">{loading ? 'Saving...' : 'Create Entry'}</button>
            <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {tab === 'entries' && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-slate-600 text-xs uppercase"><th className="p-3 text-left">Date</th><th className="p-3 text-left">Section</th><th className="p-3 text-left">Party</th><th className="p-3 text-right">Base Amt</th><th className="p-3 text-right">Rate</th><th className="p-3 text-right">TDS</th><th className="p-3 text-center">Status</th><th className="p-3"></th></tr></thead>
            <tbody>{entries.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-slate-400">No TDS entries yet</td></tr> :
              entries.map(e => (
                <tr key={e.entry_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs">{e.date}</td>
                  <td className="p-3"><span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">{e.section}</span></td>
                  <td className="p-3"><p className="font-medium">{e.party_name}</p><p className="text-xs text-slate-400">{e.party_pan}</p></td>
                  <td className="p-3 text-right font-mono">{formatCurrency(e.base_amount)}</td>
                  <td className="p-3 text-right font-mono">{e.tds_rate}%</td>
                  <td className="p-3 text-right font-mono font-medium text-orange-600">{formatCurrency(e.tds_amount)}</td>
                  <td className="p-3 text-center">{e.is_deposited ? <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Deposited</span> : <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">Pending</span>}</td>
                  <td className="p-3">{!e.is_deposited && <button onClick={() => setDepositForm({ entry_id: e.entry_id, challan_no: '', deposit_date: '', bsr_code: '' })} className="text-xs text-blue-600 hover:underline">Mark Deposited</button>}</td>
                </tr>
              ))}</tbody>
          </table>
        </div>
      )}

      {tab === 'summary' && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3">By Section</h3>
              <div className="space-y-2">{summary.by_section?.map(s => (
                <div key={s.section} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div><p className="font-medium text-sm">{s.section}</p><p className="text-xs text-slate-500">{s.description}</p></div>
                  <div className="text-right"><p className="font-mono font-bold text-sm">Rs. {formatCurrency(s.total_tds)}</p><p className="text-xs text-slate-400">{s.count} entries | {s.pending_count} pending</p></div>
                </div>
              ))}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3">By Party (Top 10)</h3>
              <div className="space-y-2">{summary.by_party?.slice(0, 10).map(p => (
                <div key={`${p.party_name}-${p.pan || 'no-pan'}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div><p className="font-medium text-sm">{p.party_name}</p><p className="text-xs text-slate-400">PAN: {p.pan || 'N/A'}</p></div>
                  <div className="text-right"><p className="font-mono font-bold text-sm">Rs. {formatCurrency(p.total_tds)}</p><p className="text-xs text-slate-400">{p.count} deductions</p></div>
                </div>
              ))}</div>
            </div>
          </div>
        </div>
      )}

      {tab === 'sections' && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-xs uppercase text-slate-600"><th className="p-3 text-left">Section</th><th className="p-3 text-left">Description</th><th className="p-3 text-right">Individual Rate</th><th className="p-3 text-right">Company Rate</th><th className="p-3 text-right">Threshold</th></tr></thead>
            <tbody>{sections.map(s => (
              <tr key={s.code} className="border-t border-slate-100"><td className="p-3 font-mono font-medium">{s.code}</td><td className="p-3">{s.description}</td><td className="p-3 text-right font-mono">{s.rate_individual}%</td><td className="p-3 text-right font-mono">{s.rate_company}%</td><td className="p-3 text-right font-mono">Rs. {formatCurrency(s.threshold)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {depositForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg p-6 w-96 space-y-4">
            <h3 className="font-heading font-semibold">Mark TDS Deposited</h3>
            <div><label className="form-label">Challan No.</label><input type="text" value={depositForm.challan_no} onChange={e => setDepositForm(s => ({ ...s, challan_no: e.target.value }))} className="form-input" /></div>
            <div><label className="form-label">Deposit Date</label><input type="date" value={depositForm.deposit_date} onChange={e => setDepositForm(s => ({ ...s, deposit_date: e.target.value }))} className="form-input" /></div>
            <div><label className="form-label">BSR Code</label><input type="text" value={depositForm.bsr_code} onChange={e => setDepositForm(s => ({ ...s, bsr_code: e.target.value }))} className="form-input" /></div>
            <div className="flex gap-2"><button onClick={() => handleDeposit(depositForm.entry_id)} className="btn-accent">Confirm</button><button onClick={() => setDepositForm(null)} className="btn-secondary">Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
